import { Hint } from 'components';
import { isSameDay } from 'date-fns';
import {
  convertDateByTimezone,
  formatToDate,
  formatToDateTimeShort,
  lodash,
} from 'helpers';
import { useIsFormEditable } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { Input, Stack, Typography } from 'libs/ui';
import { InputLabel } from 'libs/ui/Input/templates';

import { Field } from '../../../forms/orderForm';
import { OrderSection } from '../../OrderSection';

import { useExecutionPeriodSection } from './useExecutionPeriodSection';

export interface ExecutionPeriodSectionProps {
  signedAt?: {
    show: boolean;
    value?: Date;
  };

  closedDate?: {
    show: boolean;
    value?: Date;
  };
}

export const ExecutionPeriodSection = ({
  signedAt,
  closedDate,
}: ExecutionPeriodSectionProps) => {
  const { t } = useTranslation();

  const isEditable = useIsFormEditable();

  const {
    enabledYears,
    disabledDates,
    minEndDate,
    brokerTimezoneOffset,
    onChangeEndDate,
    onChangeStartDate,
  } = useExecutionPeriodSection();

  return (
    <OrderSection
      fields={lodash.compact([
        signedAt?.show && (
          <Input
            fullWidth
            readOnly
            disabled={isEditable}
            label={t(
              'order.sections.executionPeriodSection.fields.signedAt.label'
            )}
            value={
              signedAt?.value
                ? formatToDateTimeShort(
                    convertDateByTimezone(signedAt.value, brokerTimezoneOffset)
                  )
                : undefined
            }
          />
        ),
        <Field.DatePicker
          disableFuture
          fullWidth
          label={
            <Stack alignItems="center" direction="row" spacing={8}>
              <InputLabel
                label={t(
                  'order.sections.executionPeriodSection.fields.startDate.label'
                )}
              />

              <Hint
                title={
                  <Typography whiteSpace="pre-wrap">
                    {t(
                      'order.sections.executionPeriodSection.fields.startDate.hint'
                    )}
                  </Typography>
                }
              />
            </Stack>
          }
          name="startDate"
          placeholder={t(
            'order.sections.executionPeriodSection.fields.startDate.placeholder'
          )}
          shouldDisableDate={(date) =>
            disabledDates.some((d) => isSameDay(date, d))
          }
          shouldDisableYear={(year) => !enabledYears.has(year.getFullYear())}
          onChange={onChangeStartDate}
        />,
        <Field.DatePicker
          fullWidth
          showRequiredMark
          disableFuture={false}
          label={t(
            'order.sections.executionPeriodSection.fields.endDate.label'
          )}
          minDate={minEndDate}
          name="endDate"
          placeholder={t(
            'order.sections.executionPeriodSection.fields.endDate.placeholder'
          )}
          shouldDisableDate={(date) =>
            disabledDates.some((d) => isSameDay(date, d))
          }
          shouldDisableYear={(year) => !enabledYears.has(year.getFullYear())}
          onChange={onChangeEndDate}
        />,
        <Field.Quantity
          fullWidth
          disabled={isEditable}
          label={t(
            'order.sections.executionPeriodSection.fields.executionPeriodInBusinessDays.label'
          )}
          name="executionPeriodInBusinessDays"
        />,
        closedDate?.show && (
          <Input
            fullWidth
            readOnly
            disabled={isEditable}
            label={t(
              'order.sections.executionPeriodSection.fields.closedDate.label'
            )}
            value={
              closedDate?.value
                ? formatToDate(
                    convertDateByTimezone(
                      closedDate.value,
                      brokerTimezoneOffset
                    )
                  )
                : undefined
            }
          />
        ),
      ])}
      title={t('order.sections.executionPeriodSection.title')}
    />
  );
};
